/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Input 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/input/Input.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 8. 30.
 */
import * as parent from '@components/form/field/Field';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './Input.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 필드내 텍스트 정렬
     */
    inputAlign?: string;

    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    placeholder?: LanguageText;

    /**
     * 텍스트 자리수
     */
    valueLength?: number;

    /**
     * clear 버튼 여부
     */
    clearable?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Input extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'input';

    /**
     * Input 확장타입
     */
    abstract inputVariant: string;

    /**
     * 필드내 텍스트 정렬
     */
    inputAlign: string | null = '';

    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    placeholder: LanguageText | null;

    /**
     * 텍스트 자리수
     */
    valueLength: number | null;

    /**
     * clear 버튼 여부
     */
    clearable: boolean;

    /**
     * 키보드 이벤트를 활성화할지 여부
     */
    isKeyboardEnabled: boolean;

    /**
     * INPUT 필드 DOM
     */
    $input: Dom<HTMLInputElement> = null;

    /**
     * 삭제 DOM
     */
    $clear: Dom = null;

    /**
     * INPUT 필드 공용클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Input> = null) {
        super(properties);

        this.inputAlign = this.properties?.inputAlign ?? null;
        this.placeholder = this.properties?.placeholder ?? null;
        this.valueLength = this.properties?.valueLength ?? null;
        this.clearable = this.properties?.clearable === true;
    }

    /**
     * INPUT 필드 DOM 을 가져온다.
     *
     * @param events - INPUT 이벤트 설정여부 (true : 전체, false : 이벤트설정없음, string[] : 설정할 이벤트명)
     * @param variant - INPUT 타입
     * @returns INPUT 필드 DOM
     */
    $getInput(events: boolean | string[] = true, variant: string = null): Dom<HTMLInputElement> {
        if (this.$input === null) {
            this.$input = Html.create('input', {
                id: this.id + '-focusable',
                type: variant ?? this.inputVariant,
                name: this.name ?? this.id,
                autocomplete: 'off',
            });

            if (this.inputAlign !== null) {
                this.$input.setStyle('text-align', this.inputAlign);
            }

            if (this.readonly == true) {
                this.$input.setAttr('readonly', 'readonly');
            }

            if (events === true) {
                events = ['input', 'blur'];
            } else {
                events = [];
            }

            if (events.includes('input') == true) {
                this.$input.on('input', async (me) => {
                    let value = me.getValue();
                    if (this.valueLength !== null && value.length > this.valueLength) {
                        value = value.substring(0, this.valueLength);
                        me.setValue(value);
                    }
                    if (value.length == 0) {
                        value = null;
                    }
                    await this.setValue(value, false, false);
                    const validation = this.getValidationHistory(value, true);
                    this.updateValidation(validation);
                });
            }

            if (events.includes('blur') == true) {
                this.$input.on('blur', async (me) => {
                    let value = me.getValue();
                    if (value.length == 0) {
                        value = null;
                    }
                    await this.setValue(value, false, false);
                    await this.isValid(value);
                });
            }

            if (events.includes('keydown') == true) {
                this.$input.on('keydown', async (_, e) => {
                    if (e.key !== 'Escape') {
                        e.stopImmediatePropagation();
                    }
                });
            }
        }

        return this.$input;
    }

    /**
     * 초기화버튼 DOM 을 가져온다.
     *
     * @returns 초기화버튼 DOM
     */
    $getClear(events: boolean | string[] = true): Dom {
        if (this.$clear === null) {
            this.$clear = Html.create('i');

            if (events === true) {
                events = ['click'];
            } else {
                events = [];
            }

            if (events.includes('click') == true) {
                this.$clear.on('click', (_, e) => {
                    this.setValue(null);
                    this.focus();
                    e.preventDefault();
                    e.stopImmediatePropagation();
                });
            }
        }

        return this.$clear;
    }

    /**
     * 필드에 포커스를 지정한다.
     */
    focus(): void {
        if (this.isRendered() == false) {
            return;
        }

        this.$getInput().focus();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_update - INPUT 태그의 값을 변경할 지 여부
     */
    async setValue(value: any, is_init: boolean = false, is_update: boolean = true): Promise<void> {
        await this.lockValue();

        if (is_update == true) {
            this.$getInput().setValue(value?.toString() ?? '');
        }

        await this.releaseValue(value, is_init);
    }

    /**
     * 읽기전용상태를 변경한다.
     *
     * @param readonly - 읽기전용여부
     */
    setReadonly(readonly: boolean): void {
        if (readonly == true) {
            this.$getInput().setAttr('readonly', 'readonly');
        } else {
            this.$getInput().removeAttr('readonly');
        }

        super.setReadonly(readonly);
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.$getInput().setDisabled(disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();
        this.$getComponent().setAttr('data-input-variant', this.inputVariant);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        if (this.placeholder !== null) {
            this.$getInput().setAttr('placeholder', await Language.resolveText(this.placeholder));
        }
        this.$getContent().append(this.$getInput());

        if (this.clearable == true) {
            this.$getContent().append(this.$getClear());
        }
    }

    /**
     * 키보드 이벤트를 처리한다.
     *
     * @param e - 키보드 이벤트
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onKeyboard(e: KeyboardEvent): Promise<boolean> {
        await this.fireEvent('keyboard', [e, this]);
        return true;
    }
}
